import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Users,
  CurrencyInr,
  Airplane,
  EnvelopeSimple,
  Bed,
  ChatCircleDots,
  ShareNetwork,
  ChartLineUp,
  SignOut,
  ArrowsClockwise,
  DownloadSimple,
  Compass,
  Sparkle,
} from "@phosphor-icons/react";
import {
  adminMe,
  adminOverview,
  adminTop,
  adminTimeseries,
  adminRecent,
  adminEmails,
  clearAdminToken,
  getAdminToken,
} from "../lib/admin";
import { FlycherLogo } from "../components/FlycherLogo";
import { Seo } from "../components/Seo";
import { Toaster, toast } from "sonner";

const RANGES = [
  { key: "24h", label: "24h" },
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
  { key: "90d", label: "90d" },
  { key: "all", label: "All" },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [range, setRange] = useState("7d");
  const [me, setMe] = useState(null);
  const [overview, setOverview] = useState(null);
  const [top, setTop] = useState(null);
  const [series, setSeries] = useState(null);
  const [recent, setRecent] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getAdminToken()) {
      nav("/admin/login", { replace: true });
      return;
    }
    adminMe()
      .then(setMe)
      .catch(() => {
        clearAdminToken();
        nav("/admin/login", { replace: true });
      });
  }, [nav]);

  const refresh = async (r = range) => {
    setLoading(true);
    try {
      const [ov, tp, ts, rc, em] = await Promise.all([
        adminOverview(r),
        adminTop(r),
        adminTimeseries(r),
        adminRecent(80),
        adminEmails(40),
      ]);
      setOverview(ov);
      setTop(tp);
      setSeries(ts);
      setRecent(rc.rows || []);
      setEmails(em.rows || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearAdminToken();
        nav("/admin/login", { replace: true });
      } else {
        toast.error("Couldn't load analytics");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (me) refresh(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, range]);

  const logout = () => {
    clearAdminToken();
    nav("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <Seo title="Analytics dashboard" path="/admin" noindex />
      <Toaster theme="light" position="top-center" offset={20} />

      {/* Top bar */}
      <header
        className="sticky top-0 z-30 glass border-b border-slate-200"
        style={{ background: "rgba(255,255,255,0.92)" }}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <FlycherLogo size={32} />
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs font-mono text-slate-500">
              {me?.email}
            </span>
            <Link
              to="/admin/autoblog"
              data-testid="admin-autoblog-link"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-orange-500 text-orange-600 hover:bg-orange-50"
            >
              AutoBlog
            </Link>
            <button
              type="button"
              data-testid="admin-refresh"
              onClick={() => refresh(range)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
            >
              <ArrowsClockwise size={12} weight="bold" /> Refresh
            </button>
            <button
              type="button"
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white"
              style={{ background: "#0B2545" }}
            >
              <SignOut size={12} weight="bold" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10 pb-24">
        {/* Super-admin tools launcher — surfaces AutoBlog + other tools
            at the very top so it's the first thing a super-admin sees. */}
        <section
          className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4"
          data-testid="admin-tools-launcher"
        >
          <Link
            to="/admin/autoblog"
            data-testid="admin-tools-autoblog"
            className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white p-5 shadow-sm hover:shadow-xl transition-all"
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
                <Sparkle size={20} weight="fill" />
              </span>
              <span className="text-xs uppercase tracking-[0.15em] font-semibold text-white/90">
                Super Admin · SEO
              </span>
            </div>
            <div className="font-serif text-2xl md:text-3xl leading-tight mb-1">
              AutoBlog Engine
            </div>
            <p className="text-sm text-white/90 leading-snug">
              Kill-switch, 120-day plan generator, run-now, live keyword corpus (1,057 rows). Publishes twice daily.
            </p>
            <span className="absolute right-4 top-4 text-white/70 group-hover:translate-x-1 transition-transform text-xl">
              →
            </span>
          </Link>

          <div
            className="rounded-2xl bg-white border border-slate-200 p-5"
            data-testid="admin-tools-blog-link"
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-700 font-semibold">
                B
              </span>
              <span className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">
                Public feed
              </span>
            </div>
            <div className="font-serif text-2xl text-slate-900 mb-1">Blog</div>
            <p className="text-sm text-slate-600 mb-3 leading-snug">
              Preview the public blog exactly as visitors see it.
            </p>
            <Link
              to="/blog"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 hover:text-orange-700"
            >
              Open /blog →
            </Link>
          </div>

          <div
            className="rounded-2xl bg-white border border-slate-200 p-5"
            data-testid="admin-tools-sitemap-link"
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-700 font-semibold">
                S
              </span>
              <span className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">
                SEO
              </span>
            </div>
            <div className="font-serif text-2xl text-slate-900 mb-1">Sitemap</div>
            <p className="text-sm text-slate-600 mb-3 leading-snug">
              Dynamic sitemap — includes every published blog post.
            </p>
            <a
              href="/api/sitemap.xml"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 hover:text-orange-700"
            >
              Open sitemap.xml →
            </a>
          </div>
        </section>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <span className="kicker">— Cockpit</span>
            <h1 className="font-serif text-4xl sm:text-5xl mt-2 text-slate-900">
              Flycher{" "}
              <span className="italic" style={{ color: "#2A7FB8" }}>
                analytics.
              </span>
            </h1>
            <p className="text-sm text-slate-600 mt-2">
              Self-hosted, real-time. Every event stays inside your MongoDB.
            </p>
          </div>

          <div
            className="inline-flex rounded-full p-1 bg-white border border-slate-200 shadow-sm"
            data-testid="range-switcher"
          >
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                data-testid={`range-${r.key}`}
                onClick={() => setRange(r.key)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full transition ${
                  range === r.key
                    ? "text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                style={
                  range === r.key ? { background: "#2A7FB8" } : undefined
                }
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* KPI tiles */}
        <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <Kpi
            icon={Users}
            color="#2A7FB8"
            label="Unique visitors"
            value={overview?.visitors}
            sub={`${overview?.sessions || 0} sessions`}
          />
          <Kpi
            icon={Compass}
            color="#5FB3E3"
            label="Pageviews"
            value={overview?.pageviews}
            sub={`${overview?.total_events || 0} events`}
          />
          <Kpi
            icon={Airplane}
            color="#0B2545"
            label="Itineraries generated"
            value={overview?.itineraries_generated}
            sub={`${overview?.itinerary_starts || 0} started`}
          />
          <Kpi
            icon={Bed}
            color="#FF7E4D"
            label="Stay book clicks"
            value={overview?.stay_book_clicks}
            sub="Revenue proxy"
            big
          />
          <Kpi
            icon={EnvelopeSimple}
            color="#2D9D78"
            label="Emails captured"
            value={overview?.emails_captured}
            sub={`${overview?.email_skip_count || 0} skipped`}
          />
          <Kpi
            icon={DownloadSimple}
            color="#F4B942"
            label="PDF downloads"
            value={overview?.pdf_downloads}
            sub={`${overview?.pdf_gate_opens || 0} gate opens`}
          />
          <Kpi
            icon={ChatCircleDots}
            color="#2D9D78"
            label="Expert chat msgs"
            value={overview?.expert_messages}
          />
          <Kpi
            icon={ShareNetwork}
            color="#25D366"
            label="Shares"
            value={
              (overview?.whatsapp_shares || 0) + (overview?.link_copies || 0)
            }
            sub={`${overview?.whatsapp_shares || 0} WhatsApp`}
          />
        </section>

        {/* Conversion funnel */}
        <section className="mt-10">
          <SectionHeading
            icon={ChartLineUp}
            kicker="— Funnel"
            title="The conversion funnel"
            sub="From a curious visitor to a booking-ready click — where they drop."
          />
          <div className="card-surface rounded-2xl p-6 mt-4">
            <FunnelBar
              label="Visitors"
              value={overview?.visitors}
              max={overview?.visitors}
              color="#2A7FB8"
            />
            <FunnelBar
              label="Hit Itinerary builder"
              value={overview?.itinerary_starts}
              max={overview?.visitors}
              color="#3FA0D9"
              rate={overview?.rates?.visitor_to_itinerary}
            />
            <FunnelBar
              label="Generated an itinerary"
              value={overview?.itineraries_generated}
              max={overview?.visitors}
              color="#5FB3E3"
            />
            <FunnelBar
              label="Opened PDF download gate"
              value={overview?.pdf_gate_opens}
              max={overview?.visitors}
              color="#F4B942"
              rate={overview?.rates?.itinerary_to_pdf_gate}
            />
            <FunnelBar
              label="Captured email"
              value={overview?.emails_captured}
              max={overview?.visitors}
              color="#2D9D78"
              rate={overview?.rates?.pdf_gate_to_email}
            />
            <FunnelBar
              label="Clicked Book on partner (revenue!)"
              value={overview?.stay_book_clicks}
              max={overview?.visitors}
              color="#FF7E4D"
              rate={overview?.rates?.itinerary_to_stay_click}
              bold
            />
            <FunnelBar
              label="Shared trip with a friend"
              value={
                (overview?.whatsapp_shares || 0) + (overview?.link_copies || 0)
              }
              max={overview?.visitors}
              color="#25D366"
              rate={overview?.rates?.itinerary_to_share}
            />
          </div>
        </section>

        {/* Tops grid */}
        <section className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
          <TopList
            title="Top countries by itinerary"
            kicker="— Countries"
            rows={top?.top_countries_by_itinerary}
            color="#2A7FB8"
          />
          <TopList
            title="Top booking partners"
            kicker="— Revenue partners"
            rows={top?.top_partners_by_stay_click}
            color="#FF7E4D"
          />
          <TopList
            title="Top cities for stay bookings"
            kicker="— Cities"
            rows={top?.top_cities_by_stay_click}
            color="#0B2545"
          />
          <TopList
            title="Top expert chats by country"
            kicker="— Experts"
            rows={top?.top_countries_by_expert_chat}
            color="#2D9D78"
          />
        </section>

        {/* Daily activity */}
        <section className="mt-10">
          <SectionHeading
            icon={Sparkle}
            kicker="— Daily activity"
            title="Events per day"
            sub="Hover to see counts; spot launches, viral moments, and dead Tuesdays."
          />
          <div className="card-surface rounded-2xl p-6 mt-4">
            <DailyBars rows={series?.rows || []} />
          </div>
        </section>

        {/* Recent emails */}
        <section className="mt-10">
          <SectionHeading
            icon={EnvelopeSimple}
            kicker="— Email list"
            title="Latest captures"
          />
          <div className="card-surface rounded-2xl mt-4 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#FDF8EE] text-slate-500">
                <tr>
                  <Th>Email</Th>
                  <Th>Name</Th>
                  <Th>Country</Th>
                  <Th>Trip</Th>
                  <Th>Consent</Th>
                  <Th>When</Th>
                </tr>
              </thead>
              <tbody>
                {emails.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-slate-500">
                      No email captures yet.
                    </td>
                  </tr>
                )}
                {emails.map((e) => (
                  <tr
                    key={e.id || e.email}
                    className="border-t border-slate-100"
                  >
                    <Td bold>{e.email}</Td>
                    <Td>{e.name || "—"}</Td>
                    <Td>{e.country || "—"}</Td>
                    <Td muted>{e.trip_title || "—"}</Td>
                    <Td>{e.consent ? "✓" : "—"}</Td>
                    <Td muted>{shortDate(e.created_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Recent events */}
        <section className="mt-10">
          <SectionHeading
            icon={CurrencyInr}
            kicker="— Activity stream"
            title="Recent events"
            sub="Newest first."
          />
          <div className="card-surface rounded-2xl mt-4 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[#FDF8EE] text-slate-500 uppercase tracking-widest">
                <tr>
                  <Th>When</Th>
                  <Th>Event</Th>
                  <Th>Props</Th>
                  <Th>Path</Th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-slate-500">
                      No events yet — interact with the live site and refresh.
                    </td>
                  </tr>
                )}
                {recent.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <Td muted>{shortDate(r.ts)}</Td>
                    <Td bold mono>
                      {r.event}
                    </Td>
                    <Td mono>
                      <span className="text-slate-600 break-all">
                        {Object.keys(r.props || {}).length
                          ? JSON.stringify(r.props)
                          : "—"}
                      </span>
                    </Td>
                    <Td muted mono>
                      {r.path || "—"}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {loading && (
          <div className="text-center text-xs text-slate-400 py-8">
            Updating…
          </div>
        )}
      </main>
    </div>
  );
}

function Kpi({ icon: Icon, color, label, value, sub, big = false }) {
  return (
    <div
      className={`card-surface rounded-2xl p-5 ${big ? "ring-2 ring-[#FF7E4D]/30" : ""}`}
    >
      <span
        className="w-10 h-10 rounded-xl grid place-items-center"
        style={{ background: `${color}20`, color }}
      >
        <Icon size={18} weight="duotone" />
      </span>
      <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mt-4 font-semibold">
        {label}
      </div>
      <div className="font-serif text-3xl sm:text-4xl text-slate-900 mt-1">
        {value ?? "—"}
      </div>
      {sub && (
        <div className="text-[11px] font-mono text-slate-500 mt-1">{sub}</div>
      )}
    </div>
  );
}

function SectionHeading({ icon: Icon, kicker, title, sub }) {
  return (
    <div className="flex items-end justify-between gap-4">
      <div>
        <div className="kicker inline-flex items-center gap-2">
          {Icon ? <Icon size={12} weight="bold" /> : null}
          {kicker}
        </div>
        <h2 className="font-serif text-2xl sm:text-3xl text-slate-900 mt-1">
          {title}
        </h2>
        {sub && (
          <p className="text-xs sm:text-sm text-slate-500 mt-1">{sub}</p>
        )}
      </div>
    </div>
  );
}

function FunnelBar({ label, value = 0, max = 0, color, rate, bold }) {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span
          className={bold ? "font-semibold text-slate-900" : "text-slate-700"}
        >
          {label}
        </span>
        <span className="font-mono text-xs text-slate-600">
          <span className="font-semibold" style={{ color }}>
            {value}
          </span>
          {rate != null && (
            <span className="ml-2 text-slate-400">{rate}%</span>
          )}
        </span>
      </div>
      <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
}

function TopList({ kicker, title, rows = [], color }) {
  const max = rows?.[0]?.count || 1;
  return (
    <div className="card-surface rounded-2xl p-6">
      <div className="kicker">{kicker}</div>
      <h3 className="font-serif text-2xl text-slate-900 mt-1">{title}</h3>
      <div className="mt-4 space-y-2">
        {rows?.length === 0 && (
          <div className="text-sm text-slate-500">No data yet.</div>
        )}
        {rows?.map((r) => (
          <div key={r.key} className="flex items-center gap-3">
            <span className="text-sm text-slate-700 w-28 truncate capitalize">
              {String(r.key).replace(/-/g, " ")}
            </span>
            <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.max(4, (r.count / max) * 100)}%`,
                  background: color,
                }}
              />
            </div>
            <span
              className="text-xs font-mono font-semibold"
              style={{ color }}
            >
              {r.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DailyBars({ rows }) {
  // Aggregate counts per day (across all events)
  const map = new Map();
  rows.forEach((r) => {
    map.set(r.day, (map.get(r.day) || 0) + r.count);
  });
  const days = Array.from(map.entries()).sort((a, b) =>
    a[0].localeCompare(b[0]),
  );
  if (days.length === 0)
    return <div className="text-sm text-slate-500">No activity yet.</div>;
  const max = Math.max(...days.map(([, c]) => c));
  return (
    <div className="flex items-end gap-1 h-40">
      {days.map(([day, count]) => (
        <div
          key={day}
          className="flex-1 flex flex-col items-center gap-1 group"
          title={`${day}: ${count} events`}
        >
          <span className="text-[9px] font-mono text-slate-400 opacity-0 group-hover:opacity-100">
            {count}
          </span>
          <div
            className="w-full rounded-t-md"
            style={{
              height: `${Math.max(4, (count / max) * 100)}%`,
              background: "#2A7FB8",
            }}
          />
          <span className="text-[9px] font-mono text-slate-400 truncate w-full text-center">
            {day.slice(5)}
          </span>
        </div>
      ))}
    </div>
  );
}

const Th = ({ children }) => (
  <th className="text-left text-[10px] uppercase tracking-[0.18em] py-3 px-4 font-semibold">
    {children}
  </th>
);

const Td = ({ children, bold, muted, mono }) => (
  <td
    className={`py-2.5 px-4 ${bold ? "font-semibold text-slate-900" : ""} ${
      muted ? "text-slate-500" : "text-slate-700"
    } ${mono ? "font-mono text-[11px]" : ""}`}
  >
    {children}
  </td>
);

function shortDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return (
      d.toLocaleDateString("en-IN", { month: "short", day: "numeric" }) +
      " " +
      d.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    );
  } catch {
    return iso;
  }
}
